import { getAuth } from 'firebase/auth';
import { useEffect, useState } from 'react';
import { clearCachedToken, post } from 'src/api/requests';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { limitString } from 'src/utils/limitString';
import { AutoCompleteSelectBox } from './AutoCompleteSelectBox';
import { Button } from './Button';
import { Loading } from './Loading';
import { Modal } from './Modal';
import { Option } from './SelectBox';

interface EnvironmentSwitcherModalProps {
  modalOpen: boolean;
  title?: string;
  setModalOpen?: (open: boolean) => void;
}

export const EnvironmentSwitcherModal = ({
  modalOpen,
  title = 'Switch environment',
  setModalOpen = () => {},
}: EnvironmentSwitcherModalProps) => {
  const auth = getAuth();
  const { environment, updateEnvironment } = useEnvironment();
  const [currentEnvironment, setCurrentEnvironment] = useState<string>(
    environment?.envId as string,
  );

  const confirmEnvironment = async () => {
    setModalOpen(false);
    updateEnvironment({
      userId: auth.currentUser?.uid || '',
      envId: currentEnvironment,
    });
    window.location.reload();
  };

  return (
    <Modal
      title={title}
      isOpen={modalOpen}
      onClose={() => setModalOpen(false)}
      actionButton={<Button onClick={confirmEnvironment}>Confirm</Button>}
      className="w-96"
    >
      <div className="h-28">
        <EnvironmentSwitcher
          onChange={(value) => setCurrentEnvironment(value)}
          className="min-w-[140px] mt-10"
        />
      </div>
    </Modal>
  );
};

type EnvironmentSwitchProps = {
  onChange?: (value: string) => void;
  color?: 'primary' | 'secondary' | 'default';
  className?: string;
};

export const EnvironmentSwitcher = ({
  onChange,
  color,
  className,
}: EnvironmentSwitchProps) => {
  const [loading, setLoading] = useState(false);
  const {
    environment,
    environmentList,
    fetchEnvironments,
    hiddenEnvironmentsIds,
  } = useEnvironment();
  const environments: Option[] = environmentList.map((env) => ({
    value: env.id,
    label: `${limitString(env.name, 12)} (${env.id})`,
  }));
  const [selectedEnvironment, setSelectedEnvironment] = useState<string>();
  const [key, setKey] = useState<number>(0);

  const [defaultEnvironments, setDefaultEnvironments] = useState<Option[]>([]);

  const handleChange = async (envId: string) => {
    setLoading(true);

    await post(`/users/sync`, {}, { envId });

    clearCachedToken();

    setSelectedEnvironment(envId);
    onChange?.(envId);

    setLoading(false);
  };

  useEffect(() => {
    fetchEnvironments();
  }, []);

  useEffect(() => {
    setKey((prevKey) => prevKey + 1);
  }, [environmentList]);

  useEffect(() => {
    setSelectedEnvironment(environment?.envId);
  }, [environment, environments]);

  if (!environment || !environments.length || !selectedEnvironment) {
    return <Loading size={'8'} />;
  }

  if (loading) {
    return <Loading size={'8'} />;
  }

  const handleFetchOptions = async (query: string) => {
    const filteredEnvironments = environments
      .filter((env) => !hiddenEnvironmentsIds.includes(env.value))
      .filter((env) => env.label?.toLowerCase().includes(query.toLowerCase()))
      .slice(0, 5);

    setDefaultEnvironments(filteredEnvironments);
    return filteredEnvironments;
  };

  return (
    <AutoCompleteSelectBox
      disabled={loading}
      defaultOptions={defaultEnvironments}
      variant="outlined"
      color={color || 'primary'}
      size="medium"
      onChange={handleChange}
      defaultValue={environments.find(
        (env) => env.value === selectedEnvironment,
      )}
      className={className}
      key={`SelectBox--${environment?.envId}--${key}`}
      fetchOptions={handleFetchOptions}
      allowRemoveOption={false}
    />
  );
};
